import { Key, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tab, Tabs, useDisclosure } from '@heroui/react';
import { useSelector } from 'react-redux';
import { isPast, parseISO } from 'date-fns';
import { MessageComposer } from '@/features/MessageComposer';
import { getMyProfile, isProfileStaff, isProfileStudent } from '@/entities/Profile';
import { useGetTeacherAttendanceQuery } from '@/entities/TeacherAttendance';
import { FileDownloadRow } from '@/shared/ui/FileDownloadRow';
import { Section } from '@/shared/ui/Section';
import { Svg } from '@/shared/ui/Svg';
import FolderDocumentIcon from '@/shared/assets/icons/folder-document.svg';
import NoteEditIcon from '@/shared/assets/icons/note-edit.svg';
import { ILessonNote } from '@/shared/types/lesson';
import type { FileInfo } from '@/shared/types/files';
import { LessonNote } from '../LessonNote';
import { LessonAttendanceCheckModal } from '../LessonAttendanceCheckModal';

type LessonTab = 'materials' | 'notes';

type LessonTabsLesson = {
  groupLessonId?: number | null;
  scheduledAt?: string | null;
  description?: string | null;
  attachments?: FileInfo[];
  isOnlyVideoLesson?: boolean;
  order?: number;
  hasAttendanceSet?: boolean;
};

interface LessonTabsProps {
  assignmentHref: string;
  draftKey: string;
  isSubmitting: boolean;
  onCreateNote: (data: { text: string; files: File[] }) => Promise<void>;
  lessonNotes?: ILessonNote[];
  lesson?: LessonTabsLesson;
}

export const LessonTabs = ({
  assignmentHref,
  draftKey,
  isSubmitting,
  onCreateNote,
  lessonNotes = [],
  lesson,
}: LessonTabsProps) => {
  const user = useSelector(getMyProfile);
  const isStudent = isProfileStudent(user);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedTab, setSelectedTab] = useState<LessonTab>('materials');
  const navigate = useNavigate();
  const [shouldFetchAttendance, setShouldFetchAttendance] = useState(false);

  const { data: attendanceData, isFetching: isAttendanceFetching } = useGetTeacherAttendanceQuery(
    { groupLessonId: lesson?.groupLessonId ?? 0 },
    {
      skip: !isProfileStaff(user) || !shouldFetchAttendance || !lesson?.groupLessonId,
    },
  );

  const handleChangeTab = (key: Key) => setSelectedTab(key as LessonTab);

  const handleOpenAttendance = () => {
    setShouldFetchAttendance(true);
    onOpen();
  };
  const isAttendanceCheckAvailable =
    isProfileStaff(user) &&
    lesson?.groupLessonId &&
    !lesson?.isOnlyVideoLesson &&
    lesson?.scheduledAt &&
    isPast(parseISO(lesson.scheduledAt));

  const shouldHideAssignmentButton = Boolean(
    !assignmentHref || (isStudent && lesson?.scheduledAt && !isPast(parseISO(lesson.scheduledAt))),
  );

  const resolvedDescription = lesson?.description ?? null;
  const resolvedAttachments = lesson?.attachments ?? [];
  return (
    <>
      <Section className="flex flex-col gap-y-4 relative">
        <Tabs
          variant="light"
          selectedKey={selectedTab}
          onSelectionChange={handleChangeTab}
          classNames={{
            cursor: 'bg-black',
            tabContent: 'group-data-[selected=true]:text-white',
            panel: 'p-0',
          }}
        >
          <Tab
            key="materials"
            title={
              <div className="inline-flex items-center gap-x-2 py-2">
                <Svg Icon={FolderDocumentIcon} />
                <p>Materials</p>
              </div>
            }
          >
            <div className="flex flex-col gap-y-6">
              {resolvedDescription && <p>{resolvedDescription}</p>}
              <div
                className={
                  resolvedAttachments.length > 0
                    ? 'grid grid-cols-2 gap-x-8 justify-between items-center'
                    : 'flex flex-col gap-y-4'
                }
              >
                {resolvedAttachments.length > 0 && (
                  <div className="flex flex-col gap-3">
                    {resolvedAttachments.map((file) => (
                      <FileDownloadRow key={file.key} file={file} />
                    ))}
                  </div>
                )}
                <div className="flex justify-end">
                  {!shouldHideAssignmentButton && (
                    <Button
                      className="justify-self-end w-fit bg-[#211D55] text-white ml-auto"
                      radius="full"
                      onPress={() => navigate(assignmentHref)}
                    >
                      Go to assignment
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Tab>
          <Tab
            key="notes"
            title={
              <div className="inline-flex items-center gap-x-2 py-2">
                <Svg Icon={NoteEditIcon} />
                <p>Notes</p>
              </div>
            }
          >
            <div className="space-y-6">
              <MessageComposer
                draftKey={draftKey}
                isSubmitting={isSubmitting}
                onSubmit={onCreateNote}
              />
              {lessonNotes.length > 0 ? (
                <ul className="space-y-4">
                  {lessonNotes.map((note) => (
                    <LessonNote key={note.id} note={note} userId={user?.id ?? null} />
                  ))}
                </ul>
              ) : null}
            </div>
          </Tab>
        </Tabs>
        {isAttendanceCheckAvailable && (
          <Button
            variant="bordered"
            onPress={handleOpenAttendance}
            isLoading={isAttendanceFetching}
            className="absolute top-6 right-6 min-w-[152px] rounded-[8px] border-[#211D55] text-[#211D55] hover:bg-[#211D55] hover:text-white"
          >
            <span className={isAttendanceFetching ? 'hidden' : ''}>Check attendance</span>
          </Button>
        )}
      </Section>
      {!isAttendanceFetching && isOpen && lesson?.order && (
        <LessonAttendanceCheckModal
          isOpen={isOpen}
          onClose={onClose}
          attendanceData={attendanceData}
          groupLessonId={lesson?.groupLessonId}
          order={lesson.order}
          hasAttendanceSet={lesson?.hasAttendanceSet ?? false}
        />
      )}
    </>
  );
};
